/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import GroupMetadataModel from '../../../../../models/GroupMetadata';

import JoinGroupConsent from './JoinGroupConsent';
import JoinGroupEligibility from './JoinGroupEligibility';

import RouteNames from '../../../RouteNames';
import { TrackEvent } from '../../../../../utilities/UserMetrics';
import { PolicyName } from '../../../../../models/PolicyModel';
import ProfileModel from '../../../../../models/ProfileModel';
import UserModel from '../../../../../models/UserModel';
import AppUtils from '../../../../../utilities/AppUtils';
import GroupPhrases from '../../../../../phraseKeys/Group.phrase';

import { useNavigate } from '../../../../hooks/useNavigate';
import { useUIState } from '../../../../hooks/useUIState';
import { useGroupForActiveProfile } from '../../../../hooks/useGroup';
import { usePolicy } from '../../../../hooks/usePolicy';
import { useActiveUser } from '../../../../hooks/useUser';
import { useSurveysForActiveProfileGroup } from '../../../../hooks/useSurvey';
import { useActiveProfile, useProfile } from '../../../../hooks/useProfile';
import { useShareCardDefinitionList } from '../../../../hooks/useShareCard';

interface Props {
  match: any;
  location: any;
}

export const JoinGroup: React.FC<Props> = (props) => {
  const groupId = Number(props.match.params.groupId);
  const intendedJoinGroupProfileId = Number(AppUtils.getQueryParams(props.location.search).profileId);
  const backUrl = AppUtils.getQueryParams(props.location.search).backUrl;

  const [activeProfileData] = useActiveProfile();
  const profileId = intendedJoinGroupProfileId || activeProfileData.data.id;
  const [profileData, profileActions] = useProfile(profileId);

  const [agreedToCriteria, setAgreedToCriteria] = React.useState(false);
  const [, navActions] = useNavigate();
  const [uiState, uiStateActions] = useUIState();
  const [{ group }, groupActions] = useGroupForActiveProfile(groupId, true);
  const [, policyActions] = usePolicy();
  const [, surveyActions] = useSurveysForActiveProfileGroup(null);
  const [{ user }, userActions] = useActiveUser();
  const [, scDefActions] = useShareCardDefinitionList(profileId);

  React.useEffect(() => {
    uiStateActions.setActiveGroup(groupId);
  }, []);

  const joiningIntendedGroup = Boolean(intendedJoinGroupProfileId);
  const profile = new ProfileModel(profileData.data);
  const isMainProfile = uiState.isActiveProfileMainProfile;
  const groupModel = new GroupMetadataModel(group);
  const joinPhrases = GroupPhrases.join(isMainProfile, profile.greetingName());
  const phraseAccept = groupModel.eligibility.hasValue() ? joinPhrases.agreeCriteria : { id: 'groupJoinEligibility.agreeNoCriteria.button.label' };
  const phraseConfirmLabel = joinPhrases.eligibilityConfirmLabel;

  const groupPolicy = policyActions.getPolicyByName(PolicyName.GROUP_CONSENT);

  const policyIds = groupPolicy ? [groupPolicy.id] : [];

  if (agreedToCriteria) {
    return (
      <JoinGroupConsent
        groupId={groupId}
        group={group}
        onCancel={() => {
          groupActions.clearGroupSearch();
          navActions.goTo(RouteNames.myGroups());
        }}
        onGoBack={() => setAgreedToCriteria(false)}
        onAccept={() => {
          // confirm consent
          const groupMetadata = new GroupMetadataModel(group);
          TrackEvent.groups.consentConfirmed(groupMetadata.data.name);
          profileActions.provideGroupConsent(group.id, policyIds);
          // join group
          groupActions.joinGroup()
          .then(() => {
            scDefActions.refresh();

            surveyActions.requestSurveys()
              .then((surveys) => {
                const surveysTodo = surveys.filter(s => !s.submitted && s.group_id === groupId);

                if (!surveysTodo.length) {
                  navActions.goTo(RouteNames.groupInfo(groupId));
                } else if (surveysTodo.length > 0) {
                  navActions.goTo(RouteNames.surveyIntro(groupId, surveysTodo[0].id));
                } else {
                  navActions.goTo(RouteNames.surveys(groupId));
                }
              });
          }).catch(err => {
            uiStateActions.showErrorNotification(err.messageKey());
          });
        }}
      />
    );
  } else {
    return (
      <JoinGroupEligibility
        groupId={groupId}
        group={group}
        phraseAccept={phraseAccept}
        phraseConfirmLabel={phraseConfirmLabel}
        onAccept={() => {
          // confirm eligibility
          const groupMetadata = new GroupMetadataModel(group);
          TrackEvent.groups.eligibilityConfirmed(groupMetadata.data.name);

          setAgreedToCriteria(true);
        }}
        onGoBack={() => {
          if (backUrl) {
            navActions.goBackTo(backUrl);
            return;
          }

          if (joiningIntendedGroup) {
            // need to restore group join intent that was cleared on transition from the new Join Group page to this page
            userActions.apiUpdate(UserModel.addJoinGroupIntent(user, groupId))
              .then(() => {
                navActions.goBackTo(RouteNames.intendedGroup());
              });
          } else {
            navActions.goBackTo(RouteNames.findGroup());
          }
        }}
      />
    );
  }
};
